"use client";

import { useEffect } from "react";
import { flare } from "@flareapp/js";
import { shouldReloadForChunkError } from "@/lib/chunk-reload";
import { useTranslations } from "next-intl";
import { Wordmark } from "@/components/shell";
import { Button, GlassCard, LinkButton } from "@/components/ui";

/**
 * Localized client error boundary for the app locale segment. Catches render
 * and data-fetch errors below `[locale]/layout`, shows a branded fallback, and
 * offers a retry (`reset`) plus a way home. No PII is logged — just the error.
 */
export default function LocaleError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("errorBoundary");

  useEffect(() => {
    // A tab that outlived a deploy asked for a chunk the new build dropped.
    // React caught it here, so no window handler will see it — reload once and
    // skip the report, since the visitor never sees this boundary.
    if (shouldReloadForChunkError(error, window.sessionStorage)) {
      window.location.reload();
      return;
    }
    // Client-side, non-PII: the error object only. Server logs carry context.
    console.error(error);
    // Reported only when Flare is lit (instrumentation-client.ts, env-gated).
    if (process.env.NEXT_PUBLIC_FLARE_KEY) flare.report(error);
  }, [error]);

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
      <div className="mb-8">
        <Wordmark href={undefined} size="lg" />
      </div>
      <GlassCard padding="lg" className="w-full max-w-sm">
        <h1 className="font-display text-2xl font-semibold text-white">
          {t("title")}
        </h1>
        <p className="mt-3 text-sm text-white/55">{t("body")}</p>
        <div className="mt-6 flex flex-col gap-3">
          <Button variant="primary" fullWidth onClick={() => reset()}>
            {t("retry")}
          </Button>
          <LinkButton href="/" variant="ghost" fullWidth>
            {t("home")}
          </LinkButton>
        </div>
      </GlassCard>
    </main>
  );
}
